import { apiFetch } from '@/lib/api';
import type {
  IbamaCaseDetail,
  IbamaCaseList,
  IbamaCollection,
  IbamaConamaBoard,
  IbamaConamaReunion,
  IbamaConsistency,
  IbamaDecisions,
  IbamaEntities,
  IbamaEntity,
  IbamaFees,
  IbamaInstrumentTracks,
  IbamaJudge,
  IbamaJudges,
  IbamaNotificationRegister,
  IbamaOverview,
  IbamaPenalties,
  IbamaTimeline,
} from '@/services/types';

/** The fifth agency. Same envelope, same substrate contract, two populations. */
export function fetchIbamaOverview(): Promise<IbamaOverview> {
  return apiFetch<IbamaOverview>('/api/v1/ibama/overview');
}

export interface IbamaCaseQuery {
  query?: string | null;
  uf?: string | null;
  nature?: string | null;
  penalty_type?: string | null;
  /** A collection outcome code (SETTLED, JUDICIAL, ...). */
  outcome?: string | null;
  /** `none` | `first` | `board`: how far the instances got. */
  instance?: string | null;
  year?: number | null;
  /** The company's full 14-digit CNPJ; anything shorter matches nothing. */
  entity?: string | null;
  /** `company` | `person` | `unknown`. */
  subject?: string | null;
  /** `brl` | `other`. */
  currency?: string | null;
  limit?: number;
  offset?: number;
}

/**
 * The filter params, without the page bounds, so the CSV export replays the
 * SCREEN's exact query (`limit`/`offset` describe a page, never a result).
 */
export function ibamaCaseParams(query: IbamaCaseQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.uf) params.set('uf', query.uf);
  if (query.nature) params.set('nature', query.nature);
  if (query.penalty_type) params.set('penalty_type', query.penalty_type);
  if (query.outcome) params.set('outcome', query.outcome);
  if (query.instance) params.set('instance', query.instance);
  if (query.year) params.set('year', String(query.year));
  if (query.entity) params.set('entity', query.entity);
  if (query.subject) params.set('subject', query.subject);
  if (query.currency) params.set('currency', query.currency);
  return params;
}

export function fetchIbamaCases(query: IbamaCaseQuery): Promise<IbamaCaseList> {
  const params = ibamaCaseParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<IbamaCaseList>(`/api/v1/ibama/cases?${params.toString()}`);
}

/** One ledger case. `key` is `<auto number>-<UF>`; the pair is the natural key. */
export function fetchIbamaCase(key: string): Promise<IbamaCaseDetail> {
  return apiFetch<IbamaCaseDetail>(`/api/v1/ibama/c/${encodeURIComponent(key)}`);
}

export interface IbamaEntityQuery {
  query?: string | null;
  /** Off by default: the 730,000 CTF-only registrants join the list on request. */
  include_registry_only?: boolean | null;
  limit?: number;
  offset?: number;
}

export function ibamaEntityParams(query: IbamaEntityQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.query) params.set('query', query.query);
  if (query.include_registry_only) params.set('include_registry_only', 'true');
  return params;
}

export function fetchIbamaEntities(query: IbamaEntityQuery): Promise<IbamaEntities> {
  const params = ibamaEntityParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<IbamaEntities>(`/api/v1/ibama/entities?${params.toString()}`);
}

/** One company, by its full 14-digit CNPJ. Natural persons have no page. */
export function fetchIbamaEntity(cnpj: string): Promise<IbamaEntity> {
  return apiFetch<IbamaEntity>(`/api/v1/ibama/e/${encodeURIComponent(cnpj)}`);
}

// --------------------------------------------------------------------------- //
// The eight ANEEL-shaped screens
// --------------------------------------------------------------------------- //

export function fetchIbamaDecisions(): Promise<IbamaDecisions> {
  return apiFetch<IbamaDecisions>('/api/v1/ibama/decisions');
}

export function fetchIbamaPenalties(): Promise<IbamaPenalties> {
  return apiFetch<IbamaPenalties>('/api/v1/ibama/penalties');
}

export function fetchIbamaCollection(): Promise<IbamaCollection> {
  return apiFetch<IbamaCollection>('/api/v1/ibama/collection');
}

export interface IbamaGroupQuery {
  nature?: string | null;
  year?: number | null;
  /** Substring of the legal classification. */
  query?: string | null;
  limit?: number;
  offset?: number;
}

export function ibamaConsistencyParams(query: IbamaGroupQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.nature) params.set('nature', query.nature);
  if (query.year) params.set('year', String(query.year));
  if (query.query) params.set('query', query.query);
  return params;
}

export function fetchIbamaConsistency(query: IbamaGroupQuery): Promise<IbamaConsistency> {
  const params = ibamaConsistencyParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<IbamaConsistency>(`/api/v1/ibama/consistency?${params.toString()}`);
}

export function fetchIbamaTimeline(): Promise<IbamaTimeline> {
  return apiFetch<IbamaTimeline>('/api/v1/ibama/timeline');
}

export function fetchIbamaNotifications(): Promise<IbamaNotificationRegister> {
  return apiFetch<IbamaNotificationRegister>('/api/v1/ibama/notifications');
}

export function fetchIbamaFees(): Promise<IbamaFees> {
  return apiFetch<IbamaFees>('/api/v1/ibama/fees');
}

export function fetchIbamaInstruments(): Promise<IbamaInstrumentTracks> {
  return apiFetch<IbamaInstrumentTracks>('/api/v1/ibama/instruments');
}

export function fetchIbamaJudges(): Promise<IbamaJudges> {
  return apiFetch<IbamaJudges>('/api/v1/ibama/judges');
}

export function fetchIbamaJudge(slug: string): Promise<IbamaJudge> {
  return apiFetch<IbamaJudge>(`/api/v1/ibama/j/${encodeURIComponent(slug)}`);
}

export function fetchIbamaConamaBoard(): Promise<IbamaConamaBoard> {
  return apiFetch<IbamaConamaBoard>('/api/v1/ibama/conama/board');
}

export function fetchIbamaConamaReunion(reunionNumber: number): Promise<IbamaConamaReunion> {
  return apiFetch<IbamaConamaReunion>(`/api/v1/ibama/conama/reunion/${reunionNumber}`);
}

export function fetchIbamaConamaRelator(slug: string): Promise<IbamaJudge> {
  return apiFetch<IbamaJudge>(`/api/v1/ibama/conama/r/${encodeURIComponent(slug)}`);
}
